/**
 * Custom React hooks for Application Insights telemetry
 *
 * These hooks provide a declarative way to track events, metrics, and user interactions
 * in your React components.
 */

import { useEffect, useCallback, useRef } from "react"
import { useLocation } from "react-router-dom"
import { appInsightsService } from "@/services/app-insights"

/**
 * Hook to track page views automatically on route changes
 * Place this in your root App component or router setup
 */
export function usePageTracking() {
  const location = useLocation()

  useEffect(() => {
    if (appInsightsService.isEnabled()) {
      appInsightsService.trackPageView(
        document.title,
        globalThis.location.pathname + globalThis.location.search
      )
    }
  }, [location])
}

/**
 * Hook to track custom events
 * @returns trackEvent function
 *
 * @example
 * const trackEvent = useAppInsightsEvent()
 * trackEvent("ReportGenerated", { reportType: "PDF", reportId: "123" })
 */
export function useAppInsightsEvent() {
  return useCallback(
    (
      name: string,
      properties?: Record<string, string>,
      measurements?: Record<string, number>
    ) => {
      if (appInsightsService.isEnabled()) {
        appInsightsService.trackEvent(name, properties, measurements)
      }
    },
    []
  )
}

/**
 * Hook to track exceptions
 * @returns trackException function
 *
 * @example
 * const trackException = useAppInsightsException()
 * try { ... } catch (error) {
 *   trackException(error)
 * }
 */
export function useAppInsightsException() {
  return useCallback((error: Error, severityLevel?: number) => {
    if (appInsightsService.isEnabled()) {
      appInsightsService.trackException(error, severityLevel)
    }
  }, [])
}

/**
 * Hook to track metrics
 * @returns trackMetric function
 *
 * @example
 * const trackMetric = useAppInsightsMetric()
 * trackMetric("ReportLoadTime", loadTimeMs, { reportType: "PDF" })
 */
export function useAppInsightsMetric() {
  return useCallback(
    (name: string, value: number, properties?: Record<string, string>) => {
      if (appInsightsService.isEnabled()) {
        appInsightsService.trackMetric(name, value, properties)
      }
    },
    []
  )
}

/**
 * Hook to track timed events (measures duration of an operation)
 * @param eventName Name of the event to track
 * @returns Start and stop functions
 *
 * @example
 * const { startTracking, stopTracking } = useTimedEvent("ReportGeneration")
 *
 * function generateReport() {
 *   startTracking()
 *   // ... do work ...
 *   stopTracking({ reportType: "PDF" })
 * }
 */
export function useTimedEvent(eventName: string) {
  const stopFnRef = useRef<(() => void) | null>(null)

  const startTracking = useCallback(() => {
    if (appInsightsService.isEnabled()) {
      stopFnRef.current = appInsightsService.startTrackEvent(eventName)
    }
  }, [eventName])

  const stopTracking = useCallback(
    (properties?: Record<string, string>, measurements?: Record<string, number>) => {
      if (appInsightsService.isEnabled()) {
        appInsightsService.stopTrackEvent(eventName, properties, measurements)
      }
      stopFnRef.current = null
    },
    [eventName]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stopFnRef.current) {
        stopFnRef.current()
      }
    }
  }, [])

  return { startTracking, stopTracking }
}

/**
 * Hook to automatically track component mount/unmount
 * Useful for tracking user retention and time spent in different areas
 *
 * @param componentName Name of the component to track
 * @param properties Optional properties to include with the event
 *
 * @example
 * function MyComponent() {
 *   useComponentTracking("ReportListPanel")
 *   // ... rest of component
 * }
 */
export function useComponentTracking(
  componentName: string,
  properties?: Record<string, string>
) {
  const mountTimeRef = useRef<number>(Date.now())

  useEffect(() => {
    if (appInsightsService.isEnabled()) {
      mountTimeRef.current = Date.now()
      appInsightsService.trackEvent(`${componentName}_Mounted`, properties)
    }

    return () => {
      if (appInsightsService.isEnabled()) {
        const timeSpent = Date.now() - mountTimeRef.current
        appInsightsService.trackEvent(
          `${componentName}_Unmounted`,
          properties,
          { timeSpentMs: timeSpent }
        )
      }
    }
  }, [componentName, properties])
}

/**
 * Hook to track user interactions (clicks, form submissions, etc.)
 * @returns trackInteraction function
 *
 * @example
 * const trackInteraction = useUserInteraction()
 *
 * <button onClick={() => trackInteraction("GenerateReport", { format: "PDF" })}>
 *   Generate Report
 * </button>
 */
export function useUserInteraction() {
  return useCallback(
    (interactionName: string, properties?: Record<string, string>) => {
      if (appInsightsService.isEnabled()) {
        appInsightsService.trackEvent(`User_${interactionName}`, {
          ...properties,
          timestamp: new Date().toISOString(),
        })
      }
    },
    []
  )
}

/**
 * Hook to set authenticated user context
 * Call this when user logs in to associate telemetry with specific users
 *
 * @example
 * const { setUser, clearUser } = useAuthenticatedUser()
 *
 * // On login
 * setUser("user@example.com", "account123")
 *
 * // On logout
 * clearUser()
 */
export function useAuthenticatedUser() {
  const setUser = useCallback((userId: string, accountId?: string) => {
    if (appInsightsService.isEnabled()) {
      appInsightsService.setAuthenticatedUserContext(userId, accountId)
    }
  }, [])

  const clearUser = useCallback(() => {
    if (appInsightsService.isEnabled()) {
      appInsightsService.clearAuthenticatedUserContext()
    }
  }, [])

  return { setUser, clearUser }
}
